import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import apiClient from '../../core/apiClient';
import './AuthPages.css';

interface EnterpriseSignupForm {
    company_name: string;
    official_email: string;
    phone_number: string;
    country_code: string;
    gst_number: string;
    company_type: string;
    company_size: string;
    website: string;
    password: string;
    confirm_password: string;
}

export const EnterpriseSignupPage: React.FC = () => {
    const navigate = useNavigate();
    const [formData, setFormData] = useState<EnterpriseSignupForm>({
        company_name: '',
        official_email: '',
        phone_number: '',
        country_code: '+91',
        gst_number: '',
        company_type: 'Private Limited',
        company_size: '1-10',
        website: '',
        password: '',
        confirm_password: ''
    });

    const [verificationState, setVerificationState] = useState({
        emailSent: false,
        emailVerified: false,
        emailOtp: '',
        phoneSent: false,
        phoneVerified: false,
        phoneOtp: '',
        docUploaded: false,
        docFileUrl: '',
        docFileName: '',
        termsAccepted: false
    });

    const [loading, setLoading] = useState({
        email: false,
        phone: false,
        doc: false,
        submit: false
    });

    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        setError('');
    };

    const handleEmailAction = async () => {
        if (!formData.official_email) {
            setError('Please enter your official email first');
            return;
        }

        // Simple regex for email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.official_email)) {
            setError('Please enter a valid official email address');
            return;
        }

        if (verificationState.emailVerified) return;

        setLoading(prev => ({ ...prev, email: true }));
        setError('');
        try {
            if (!verificationState.emailSent) {
                if (!formData.company_name) {
                    setError('Please enter company name before sending OTP');
                    setLoading(prev => ({ ...prev, email: false }));
                    return;
                }
                const response = await apiClient.post('/api/auth/initiate-signup', {
                    email: formData.official_email,
                    phone_number: formData.phone_number || '0000000000',
                    company_name: formData.company_name,
                    user_type: 'enterprise'
                });
                setVerificationState(prev => ({ ...prev, emailSent: true }));
                setSuccess('OTP sent to official email.');
            } else {
                await apiClient.post('/api/auth/verify-email', {
                    email: formData.official_email,
                    code: verificationState.emailOtp
                });
                setVerificationState(prev => ({ ...prev, emailVerified: true }));
                setSuccess('Email verified successfully');
            }
        } catch (err: any) {
            setError(err.response?.data?.message || 'Email verification failed');
        } finally {
            setLoading(prev => ({ ...prev, email: false }));
        }
    };

    const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setLoading(prev => ({ ...prev, doc: true }));
        setError('');
        const uploadData = new FormData();
        uploadData.append('file', file);

        try {
            const res = await apiClient.post('/api/upload', uploadData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            const fileUrl = res.data?.data?.file_url || res.data?.file_url;
            setVerificationState(prev => ({
                ...prev,
                docUploaded: true,
                docFileUrl: fileUrl,
                docFileName: file.name
            }));
            setSuccess('Business proof uploaded successfully');
        } catch (err: any) {
            setError(err.response?.data?.message || 'Upload failed');
        } finally {
            setLoading(prev => ({ ...prev, doc: false }));
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!verificationState.emailVerified || !verificationState.docUploaded) {
            setError('Please complete all verifications before creating enterprise account');
            return;
        }
        if (formData.password !== formData.confirm_password) {
            setError('Passwords do not match');
            return;
        }
        if (!verificationState.termsAccepted) {
            setError('Please accept the terms and conditions');
            return;
        }

        setLoading(prev => ({ ...prev, submit: true }));
        setError('');

        try {
            await apiClient.post('/api/auth/signup', {
                ...formData,
                user_type: 'enterprise',
                company_proof_file_url: verificationState.docFileUrl,
                email_otp: verificationState.emailOtp
            });
            navigate('/login');
        } catch (err: any) {
            setError(err.response?.data?.message || 'Enterprise signup failed');
        } finally {
            setLoading(prev => ({ ...prev, submit: false }));
        }
    };

    return (
        <div className="auth-container">
            <div className="auth-card enterprise-signup-card">
                <div className="signup-split-layout">
                    {/* Form Side */}
                    <div className="signup-form-side">
                        <div className="auth-header">
                            <h1>Enterprise Signup</h1>
                            <p>Empower your organization with ExiBrain's enterprise solutions.</p>
                            <div className="note-box">
                                <p><i className="fas fa-shield-alt"></i> Complete your organization's verification to unlock full enterprise capabilities.</p>
                            </div>
                        </div>

                        {error && <div className="error-message"><i className="fas fa-exclamation-circle"></i> {error}</div>}
                        {success && <div className="success-message"><i className="fas fa-check-circle"></i> {success}</div>}

                        <form onSubmit={handleSubmit} className="auth-form">
                            {/* Organization Details */}
                            <div className="form-section">
                                <div className="form-section-header">
                                    <i className="fas fa-building"></i>
                                    <h3>Organization Details</h3>
                                </div>
                                <div className="form-group">
                                    <label>Company Name</label>
                                    <input
                                        type="text"
                                        name="company_name"
                                        placeholder="ExiBrain Corp"
                                        value={formData.company_name}
                                        onChange={handleChange}
                                        required
                                    />
                                </div>

                                <div className="inline-verification">
                                    <div className="form-group">
                                        <label>Official Email</label>
                                        <input
                                            type="email"
                                            name="official_email"
                                            placeholder="admin@company.com"
                                            value={formData.official_email}
                                            onChange={handleChange}
                                            disabled={verificationState.emailVerified}
                                            required
                                        />
                                    </div>
                                    <button
                                        type="button"
                                        className={`btn ${verificationState.emailVerified ? 'btn-success' : 'btn-secondary'}`}
                                        onClick={handleEmailAction}
                                        disabled={loading.email || !formData.official_email || (verificationState.emailSent && !verificationState.emailOtp && !verificationState.emailVerified)}
                                    >
                                        {loading.email ? '...' : (verificationState.emailVerified ? 'Verified ✓' : (verificationState.emailSent ? 'Verify' : 'Send OTP'))}
                                    </button>
                                </div>
                                {verificationState.emailSent && !verificationState.emailVerified && (
                                    <div className="verification-control">
                                        <label>Enter Email OTP</label>
                                        <div className="otp-input-wrapper">
                                            <input
                                                type="text"
                                                maxLength={6}
                                                placeholder="000000"
                                                value={verificationState.emailOtp}
                                                onChange={(e) => setVerificationState(prev => ({ ...prev, emailOtp: e.target.value }))}
                                            />
                                        </div>
                                    </div>
                                )}

                                <div className="form-row">
                                    <div className="form-group" style={{ flex: '0 0 100px', minWidth: '80px' }}>
                                        <label>Code</label>
                                        <select name="country_code" value={formData.country_code} onChange={handleChange}>
                                            <option value="+91">+91</option>
                                            <option value="+1">+1</option>
                                            <option value="+44">+44</option>
                                        </select>
                                    </div>
                                    <div className="form-group">
                                        <label>Phone Number</label>
                                        <input
                                            type="tel"
                                            name="phone_number"
                                            placeholder="9876543210"
                                            value={formData.phone_number}
                                            onChange={handleChange}
                                            required
                                        />
                                    </div>
                                </div>

                                <div className="form-row">
                                    <div className="form-group">
                                        <label>GST Number</label>
                                        <input
                                            type="text"
                                            name="gst_number"
                                            placeholder="22AAAAA0000A1Z5"
                                            value={formData.gst_number}
                                            onChange={handleChange}
                                            required
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Website (Optional)</label>
                                        <input
                                            type="url"
                                            name="website"
                                            placeholder="https://company.com"
                                            value={formData.website}
                                            onChange={handleChange}
                                        />
                                    </div>
                                </div>

                                <div className="form-row">
                                    <div className="form-group">
                                        <label>Company Type</label>
                                        <select name="company_type" value={formData.company_type} onChange={handleChange}>
                                            <option value="Private Limited">Private Limited</option>
                                            <option value="Public Limited">Public Limited</option>
                                            <option value="Partnership">Partnership</option>
                                            <option value="Proprietorship">Proprietorship</option>
                                            <option value="NGO">NGO</option>
                                        </select>
                                    </div>
                                    <div className="form-group">
                                        <label>Company Size</label>
                                        <select name="company_size" value={formData.company_size} onChange={handleChange}>
                                            <option value="1-10">1-10 employees</option>
                                            <option value="11-50">11-50 employees</option>
                                            <option value="51-200">51-200 employees</option>
                                            <option value="201-500">201-500 employees</option>
                                            <option value="500+">500+ employees</option>
                                        </select>
                                    </div>
                                </div>
                            </div>

                            {/* Verification Document */}
                            <div className="form-section">
                                <div className="form-section-header">
                                    <i className="fas fa-file-contract"></i>
                                    <h3>Verification Document</h3>
                                </div>
                                <div className="upload-area" onClick={() => document.getElementById('doc-upload')?.click()}>
                                    <i className="fas fa-cloud-upload-alt"></i>
                                    <p><strong>Upload Business Proof</strong></p>
                                    <p className="form-hint">PAN, CIN, Registration Certificate (JPG, PNG, PDF)</p>
                                    <input
                                        id="doc-upload"
                                        type="file"
                                        hidden
                                        accept=".jpg,.jpeg,.png,.pdf"
                                        onChange={handleFileUpload}
                                    />
                                </div>
                                {verificationState.docUploaded && (
                                    <div className="file-preview">
                                        <span><i className="fas fa-file-alt"></i> {verificationState.docFileName}</span>
                                        <span className="status-badge verified">Uploaded</span>
                                    </div>
                                )}
                                <p className="form-hint">Make sure the document is clear and all details are visible.</p>
                            </div>

                            {/* Security */}
                            <div className="form-section">
                                <div className="form-section-header">
                                    <i className="fas fa-shield-alt"></i>
                                    <h3>Security</h3>
                                </div>
                                <div className="form-row">
                                    <div className="form-group">
                                        <label>Create Password</label>
                                        <input
                                            type="password"
                                            name="password"
                                            placeholder="••••••••"
                                            value={formData.password}
                                            onChange={handleChange}
                                            required
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Confirm Password</label>
                                        <input
                                            type="password"
                                            name="confirm_password"
                                            placeholder="••••••••"
                                            value={formData.confirm_password}
                                            onChange={handleChange}
                                            required
                                        />
                                    </div>
                                </div>
                                <div className="form-group" style={{ flexDirection: 'row', alignItems: 'center', gap: '0.5rem', marginTop: '1rem' }}>
                                    <input
                                        type="checkbox"
                                        id="ent-terms"
                                        checked={verificationState.termsAccepted}
                                        onChange={(e) => setVerificationState(prev => ({ ...prev, termsAccepted: e.target.checked }))}
                                        style={{ width: 'auto' }}
                                    />
                                    <label htmlFor="ent-terms" style={{ fontWeight: 'normal' }}>
                                        I agree to the <Link to="/terms" style={{ color: '#0284c7' }}>Enterprise Terms and Conditions</Link>
                                    </label>
                                </div>
                            </div>

                            <button
                                type="submit"
                                className="btn btn-primary btn-block signup-submit-btn"
                                disabled={loading.submit}
                            >
                                {loading.submit ? 'Creating Enterprise Account...' : 'Create Enterprise Account'}
                            </button>

                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', justifyContent: 'space-between', marginTop: '1.5rem' }}>
                                <Link to="/signup" style={{ color: '#64748b', fontSize: '0.9rem' }}><i className="fas fa-arrow-left"></i> Back to selection</Link>
                                <p style={{ color: '#64748b', fontSize: '0.9rem', margin: 0 }}>
                                    Already have an account? <Link to="/login" style={{ color: '#0284c7', fontWeight: '600' }}>Sign In</Link>
                                </p>
                            </div>
                        </form>
                    </div>

                    {/* Info Side */}
                    <div className="signup-info-side">
                        <h2>Scale with ExiBrain</h2>
                        <p className="info-subtitle">
                            Join thousands of enterprises using our platform to drive growth and efficiency.
                        </p>
                        
                        <div className="benefits-list">
                            <div className="benefit-item">
                                <i className="fas fa-check-circle"></i>
                                <div className="benefit-content">
                                    <h4>Trusted & Secure</h4>
                                    <p>Enterprise-grade security and compliance out of the box.</p>
                                </div>
                            </div>
                            <div className="benefit-item">
                                <i className="fas fa-bolt"></i>
                                <div className="benefit-content">
                                    <h4>Access Premium Features</h4>
                                    <p>Unlock advanced analytics, AI orchestration, and more.</p>
                                </div>
                            </div>
                            <div className="benefit-item">
                                <i className="fas fa-users"></i>
                                <div className="benefit-content">
                                    <h4>Team Collaboration</h4>
                                    <p>Manage users, roles, and permissions across your organization.</p>
                                </div>
                            </div>
                            <div className="benefit-item">
                                <i className="fas fa-headset"></i>
                                <div className="benefit-content">
                                    <h4>Priority Support</h4>
                                    <p>24/7 dedicated support for all your enterprise needs.</p>
                                </div>
                            </div>
                        </div>

                        <div style={{ marginTop: '2rem', padding: '1.5rem', background: 'rgba(255,255,255,0.1)', borderRadius: '12px' }}>
                            <p style={{ fontStyle: 'italic', fontSize: '0.95rem', margin: 0 }}>
                                "ExiBrain has transformed how we handle our financial forensics. The AI integration is a game-changer."
                            </p>
                            <p style={{ fontWeight: '600', marginTop: '1rem', fontSize: '0.9rem', marginBottom: 0 }}>
                                — CTO, Global Finance Corp
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
